import { z } from 'zod/v4';

const IdDTO = z.string().min(1);
export const GraphPositionDTO = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
});
export const GraphFrameSizeDTO = z.object({
  width: z.number().finite().positive(),
  height: z.number().finite().positive(),
});
const ElementDTO = z.object({
  classes: z.array(z.string()).default([]),
  selectable: z.boolean().optional(),
});
const NodeDTO = ElementDTO.extend({
  data: z.object({ id: IdDTO, parent: IdDTO.optional() }).catchall(z.json()),
  position: GraphPositionDTO,
});
const EdgeDTO = ElementDTO.extend({
  data: z
    .object({ id: IdDTO, source: IdDTO, target: IdDTO })
    .catchall(z.json()),
});
const GroupDTO = z.object({
  kind: z.enum(['wrapper', 'from-node']),
  collapsed: z.boolean(),
  frameSize: GraphFrameSizeDTO.optional(),
  suppressedEdgeIds: z.array(IdDTO).default([]),
});
const EdgeBundleDTO = z.object({
  memberIds: z.array(IdDTO).min(1),
  dominantLink: z.string().optional(),
});

/** Full canonical graph, including hidden elements. Renderer caches are derived. */
export const GraphViewSnapshotDTO = z
  .object({
    version: z.literal(1),
    nodes: z.array(NodeDTO),
    edges: z.array(EdgeDTO),
    viewState: z.object({
      groups: z.record(IdDTO, GroupDTO),
      edgeBundles: z.record(IdDTO, EdgeBundleDTO),
    }),
  })
  .superRefine((snapshot, ctx) => {
    const fail = (message: string) => ctx.addIssue({ code: 'custom', message });
    const nodes = new Map(snapshot.nodes.map((node) => [node.data.id, node]));
    const edges = new Set(snapshot.edges.map((edge) => edge.data.id));
    const groups = snapshot.viewState.groups;
    const bundles = snapshot.viewState.edgeBundles;
    const ids = [...nodes.keys(), ...edges, ...Object.keys(bundles)];
    if (
      nodes.size !== snapshot.nodes.length ||
      edges.size !== snapshot.edges.length ||
      new Set(ids).size !== ids.length
    )
      fail('Element IDs must be unique');
    for (const node of snapshot.nodes) {
      const seen = new Set([node.data.id]);
      let parent = node.data.parent;
      while (parent) {
        if (!nodes.has(parent) || !groups[parent]) {
          fail('Parent must reference a group');
          break;
        }
        if (seen.has(parent)) {
          fail('Group hierarchy must be acyclic');
          break;
        }
        seen.add(parent);
        parent = nodes.get(parent)?.data.parent;
      }
    }
    for (const edge of snapshot.edges)
      if (!nodes.has(edge.data.source) || !nodes.has(edge.data.target))
        fail('Edge endpoints must exist');
    for (const [id, group] of Object.entries(groups)) {
      if (!nodes.has(id)) fail('Group node must exist');
      if (
        new Set(group.suppressedEdgeIds).size !==
          group.suppressedEdgeIds.length ||
        group.suppressedEdgeIds.some((edge) => !edges.has(edge))
      )
        fail('Suppressed edges must reference unique original edges');
    }
    const owned = new Set<string>();
    const visit = (id: string, ancestors: Set<string>) => {
      if (ancestors.has(id)) {
        fail('Edge bundles must be acyclic');
        return;
      }
      const bundle = bundles[id];
      if (!bundle) return;
      const next = new Set([...ancestors, id]);
      for (const member of bundle.memberIds) {
        if (!edges.has(member) && !bundles[member])
          fail('Bundle members must exist');
        visit(member, next);
      }
    };
    for (const [id, bundle] of Object.entries(bundles)) {
      for (const member of bundle.memberIds) {
        if (owned.has(member)) fail('An edge can belong to only one bundle');
        owned.add(member);
      }
      visit(id, new Set());
    }
  });

export type GraphViewSnapshot = z.infer<typeof GraphViewSnapshotDTO>;
export type GraphFrameSize = z.infer<typeof GraphFrameSizeDTO>;
